import {
  ColDef,
  ColGroupDef,
  EditableCallbackParams,
  ICellRendererParams,
  IRowNode,
  ValueGetterParams,
  ValueSetterParams,
} from 'ag-grid-community'
import { useMemo } from 'react'
import { intl } from '../../../../i18n'
import { useRowNumber } from '../../../page-properties/bulksheet-properties/rowNumber'
import { useCode } from '../../../page-properties/bulksheet-properties/common'
import {
  useCreatedAt,
  useCreatedBy,
  useRevision,
  useUpdatedAt,
  useUpdatedBy,
} from '../../../page-properties/bulksheet-properties/changeLog'
import { ColumnType } from '../../../containers/commons/AgGrid'
import DateCell from '../../../containers/commons/AgGrid/components/cell/common/date'
import { PropertyType } from '../../../../lib/commons/appFunction'
import { FALSE, TRUE } from '../../../../utils/boolExpression'
import { EntitySearchCellRenderer } from '../../../containers/BulkSheetView/components/cellRenderer'
import { SelectCellEditor } from '../../../containers/BulkSheetView/components/cellEditor/SelectCellEditor'
import { EntitySearchCellEditor } from '../../../containers/BulkSheetView/components/cellEditor/EntitySearchCellEditor'
import { MultiAutocompleteCellEditor } from '../../../containers/BulkSheetView/components/cellEditor/MultiAutocompleteCellEditor'
import {
  ClientSideSelectFilter,
  ClientSideTextFilter,
} from '../../../containers/BulkSheetView/components/filter'
import userApi, { UserBasic } from '../../../../lib/functions/user'
import { DivisionProps } from '../../../../lib/functions/division'
import {
  ProgramOverviewDto,
  ProgramStatus,
} from '../../../../infrastructure/api/programs'
import { ProgramRow, validateScheduledDateOrder } from '../model/programRow'
import { isProgramEditable } from '../model/programPermission'
import { CollaboratorsCellRenderer } from './CollaboratorsCellRenderer'
import { TransitionIconsCellRenderer } from './TransitionIconsCellRenderer'

type ProgramColumnDef = ColDef | ColGroupDef<ProgramOverviewDto>

export type ProgramGridContext = {
  programStatuses: readonly ProgramStatus[]
  divisions: DivisionProps[]
  currentUserUuid?: string
  isAdmin: boolean
}

const searchUsers = (text: string) =>
  text ? userApi.search(text) : userApi.searchAll()

const editableForOperator = (
  params: EditableCallbackParams<ProgramRow>
): boolean => {
  if (!params.data) return false
  const context: ProgramGridContext = params.context
  return isProgramEditable(
    params.data,
    context.currentUserUuid,
    context.isAdmin
  )
}

export const useColumnDefs = (): ProgramColumnDef[] => {
  const rowNumberCol = useRowNumber()
  const codeCol = useCode('code')
  const revisionCol = useRevision('revision')
  const createdByCol = useCreatedBy('createdBy')
  const createdAtCol = useCreatedAt('createdAt')
  const updatedByCol = useUpdatedBy('updatedBy')
  const updatedAtCol = useUpdatedAt('updatedAt')

  return useMemo<ProgramColumnDef[]>(
    () => [
      {
        field: 'uuid',
        hide: true,
        suppressColumnsToolPanel: true,
      },
      { ...rowNumberCol, pinned: 'left' as const },
      {
        colId: 'transitionIcons',
        headerName: '',
        width: 60,
        sortable: false,
        pinned: 'left',
        cellRenderer: TransitionIconsCellRenderer,
      },
      {
        headerName: intl.formatMessage({
          id: 'programList.list.headerGroup.basic',
        }),
        children: [
          {
            ...codeCol,
            hide: false,
            pinned: 'left',
            width: 130,
            cellRendererParams: {
              ...codeCol.cellRendererParams,
              uiMeta: {
                ...codeCol.cellRendererParams?.uiMeta,
                requiredIf: FALSE,
              },
            },
            cellEditorParams: {
              ...codeCol.cellEditorParams,
              uiMeta: {
                ...codeCol.cellEditorParams?.uiMeta,
                requiredIf: FALSE,
              },
            },
          },
          {
            field: 'status',
            headerName: intl.formatMessage({
              id: 'programList.list.column.status',
            }),
            width: 130,
            pinned: 'left',
            editable: editableForOperator,
            cellRendererParams: { uiMeta: { requiredIf: TRUE } },
            cellEditor: SelectCellEditor,
            cellEditorParams: (params: { context: ProgramGridContext }) => ({
              options: params.context.programStatuses.map(status => ({
                value: status,
                label: status,
              })),
            }),
            filter: ClientSideSelectFilter,
            floatingFilter: true,
            // グリッド上に実在する値のみを選択肢にするため、optionKey/getValuesForOptionFilter は指定しない
            filterParams: {
              getValue: (v?: ProgramStatus | null) => v ?? '',
              getLabel: (v?: ProgramStatus | null) => v ?? '',
              // 定義順(STANDBY/INPROGRESS/SUSPENDED/COMPLETED)で表示する
              sortValues: (
                _uiMeta: unknown,
                options: ProgramStatus[],
                context: ProgramGridContext
              ) =>
                [...options].sort(
                  (a, b) =>
                    context.programStatuses.indexOf(a) -
                    context.programStatuses.indexOf(b)
                ),
            },
          },
          {
            colId: 'division',
            headerName: intl.formatMessage({
              id: 'programList.list.column.division',
            }),
            width: 160,
            pinned: 'left',
            editable: editableForOperator,
            cellRenderer: EntitySearchCellRenderer,
            // チーム列等と同様に文字入力で選択肢を絞り込めるようにする
            cellEditor: EntitySearchCellEditor,
            cellEditorParams: { entity: 'divisions' },
            valueGetter: (params: ValueGetterParams<ProgramOverviewDto>) =>
              params.data?.division ?? undefined,
            valueFormatter: (params: { data?: ProgramOverviewDto }) =>
              params.data?.division?.displayName ?? '',
            valueSetter: (params: ValueSetterParams<ProgramOverviewDto>) => {
              const context: ProgramGridContext = params.context
              // エディタからは部門オブジェクト、クリップボード貼り付けからは uuid が渡される
              const newValue: DivisionProps | string | null | undefined =
                params.newValue
              const uuid =
                typeof newValue === 'string' ? newValue : newValue?.uuid
              params.data.division =
                context.divisions.find(d => d.uuid === uuid) ?? null
              return true
            },
            comparator: (a?: DivisionProps | null, b?: DivisionProps | null) =>
              (a?.displayName ?? '').localeCompare(b?.displayName ?? ''),
            filter: ClientSideSelectFilter,
            floatingFilter: true,
            // グリッド上に実在する値のみを選択肢にするため、optionKey/getValuesForOptionFilter は指定しない
            filterParams: {
              valueGetter: ({ node }: { node: IRowNode<ProgramOverviewDto> }) =>
                node?.data?.division ?? undefined,
              getValue: (v?: DivisionProps | null) => v?.uuid ?? '',
              getLabel: (v?: DivisionProps | null) => v?.displayName ?? '',
            },
          },
          {
            field: 'displayName',
            headerName: intl.formatMessage({
              id: 'programList.list.column.displayName',
            }),
            width: 280,
            pinned: 'left',
            editable: editableForOperator,
            cellRendererParams: { uiMeta: { requiredIf: TRUE } },
            filter: ClientSideTextFilter,
            floatingFilter: true,
          },
        ],
      },
      {
        headerName: intl.formatMessage({
          id: 'programList.list.headerGroup.period',
        }),
        children: [
          {
            field: 'scheduledStartDate',
            headerName: intl.formatMessage({
              id: 'programList.list.column.scheduledStartDate',
            }),
            type: [ColumnType.date],
            width: 130,
            editable: editableForOperator,
            cellEditorParams: { uiMeta: { propertyType: PropertyType.Date } },
            filter: DateCell.cellFilter,
            floatingFilter: true,
          },
          {
            field: 'scheduledEndDate',
            headerName: intl.formatMessage({
              id: 'programList.list.column.scheduledEndDate',
            }),
            type: [ColumnType.date],
            width: 130,
            editable: editableForOperator,
            cellEditorParams: { uiMeta: { propertyType: PropertyType.Date } },
            cellRendererParams: {
              validate: (
                _value: unknown,
                params: ICellRendererParams<ProgramRow>
              ) =>
                params.data
                  ? validateScheduledDateOrder(params.data)
                  : undefined,
            },
            filter: DateCell.cellFilter,
            floatingFilter: true,
          },
        ],
      },
      {
        field: 'programManager',
        headerName: intl.formatMessage({
          id: 'programList.list.column.programManager',
        }),
        width: 180,
        editable: editableForOperator,
        cellRenderer: EntitySearchCellRenderer,
        cellRendererParams: { uiMeta: { requiredIf: TRUE } },
        cellEditor: EntitySearchCellEditor,
        cellEditorParams: {
          search: searchUsers,
        },
        filter: ClientSideSelectFilter,
        floatingFilter: true,
        filterParams: {
          getValue: (v?: UserBasic) => v?.name ?? '',
          getLabel: (v?: UserBasic) => v?.name ?? '',
        },
      },
      {
        field: 'collaborators',
        headerName: intl.formatMessage({
          id: 'programList.list.column.collaborators',
        }),
        width: 180,
        editable: editableForOperator,
        cellRenderer: CollaboratorsCellRenderer,
        cellEditor: MultiAutocompleteCellEditor,
        cellEditorParams: {
          search: searchUsers,
          label: (option: UserBasic) => option.name,
        },
        filter: ClientSideSelectFilter,
        floatingFilter: true,
        filterParams: {
          getValue: (v?: UserBasic) => v?.name ?? '',
          getLabel: (v?: UserBasic) => v?.name ?? '',
        },
      },
      {
        headerName: intl.formatMessage({ id: 'changeLog' }),
        children: [
          revisionCol,
          createdByCol,
          createdAtCol,
          updatedByCol,
          updatedAtCol,
        ],
      },
    ],
    [
      rowNumberCol,
      codeCol,
      revisionCol,
      createdByCol,
      createdAtCol,
      updatedByCol,
      updatedAtCol,
    ]
  )
}
